import { forwardRef } from "react";

import { ChevronDown } from "lucide-react";

import { ULThemeButton } from "@/components/ULThemeButton";
import { cn } from "@/lib/utils";

export interface ULThemeCountryCodePickerProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Selected country information
   */
  selectedCountry?:
    | {
        name: string;
        code: string; // Country code like "DZ"
        dialCode: string; // Phone code like "+213"
        flag: string; // Flag emoji or URL
      }
    | undefined;
  /**
   * Placeholder text when no country is selected
   */
  placeholder?: string;
  /**
   * Full width styling
   */
  fullWidth?: boolean;
  /**
   * When true, shows only the dial code (e.g. "+47") instead of the full
   * country name and code.
   */
  compact?: boolean;
  /**
   * Optional label rendered above the picker, matching the static label
   * used above adjacent fields (e.g. the phone number field).
   */
  label?: string;
}

const ULThemeCountryCodePicker = forwardRef<HTMLButtonElement, ULThemeCountryCodePickerProps>(
  (
    {
      selectedCountry,
      placeholder = "Select Country",
      fullWidth = false,
      compact = false,
      label,
      className,
      disabled,
      style,
      ...rest
    },
    ref,
  ) => {
    const widthStyles = fullWidth ? "w-full" : "";
    const disabledStyles = disabled ? "disabled:opacity-70 cursor-not-allowed" : "cursor-pointer";

    // ULThemeButton's outline variant hardcodes p-6, which overrides its own h-10
    // and renders taller than the h-14 input fields it needs to sit flush with.
    // className can't win that fight (ULThemeButton's own overrides always merge in
    // after it), so pin the height/vertical padding here via inline style instead.
    const compactStyle = compact
      ? {
          height: "3.5rem",
          paddingTop: 0,
          paddingBottom: 0,
          paddingLeft: "0.75rem",
          paddingRight: "0.75rem",
          ...style,
        }
      : style;

    return (
      <div>
        {label && (
          <label className="mb-2 block text-sm theme-universal:font-bold theme-universal:text-body-text theme-universal:text-(length:--ul-theme-font-body-text-size)">
            {label}
          </label>
        )}
        <ULThemeButton
          ref={ref}
          type="button"
          variant="outline"
          disabled={disabled}
          style={compactStyle}
          className={cn(
            // Override ULThemeButton styles for country picker specific styling
            compact ? "justify-center" : "justify-between",
            "text-left font-medium",
            "theme-universal:bg-input-bg",
            "theme-universal:text-input-text",
            "theme-universal:border-input-border",
            "theme-universal:text-(length:--ul-theme-font-body-text-size)",
            "theme-universal:font-body",
            "theme-universal:rounded-input",
            "theme-universal:hover:border-base-focus",
            widthStyles,
            disabledStyles,
            className,
          )}
          {...rest}
        >
          <div className={cn("flex items-center space-x-3 min-w-0", !compact && "flex-1")}>
            {selectedCountry ? (
              <>
                {/* Flag */}
                {!compact && (
                  <div className="flex-shrink-0 w-6 h-4 flex items-center justify-center">
                    {selectedCountry.flag.startsWith("http") ? (
                      <img
                        src={selectedCountry.flag}
                        alt={`${selectedCountry.name} flag`}
                        className="w-6 h-4 object-cover rounded-sm"
                      />
                    ) : (
                      <span className="text-font-base">{selectedCountry.flag}</span>
                    )}
                  </div>
                )}

                {/* Country Info */}
                <div className={cn("min-w-0", !compact && "flex-1")}>
                  <span className="theme-universal:text-input-text theme-universal:font-body truncate">
                    {compact
                      ? selectedCountry.dialCode
                      : `${selectedCountry.name}, ${selectedCountry.code}, ${selectedCountry.dialCode}`}
                  </span>
                </div>
              </>
            ) : (
              <>
                {/* Placeholder state */}
                <div className="flex-shrink-0 w-6 h-4 theme-universal:bg-input-border rounded-sm"></div>
                <span className="theme-universal:text-input-labels flex-1 truncate">
                  {placeholder}
                </span>
              </>
            )}
          </div>

          {/* Chevron */}
          <ChevronDown className={cn("w-4 h-4 theme-universal:text-input-labels")} />
        </ULThemeButton>
      </div>
    );
  },
);

ULThemeCountryCodePicker.displayName = "ULThemeCountryCodePicker";

export default ULThemeCountryCodePicker;
